import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { VERTICALS, VERTICAL_ORDER } from '@/data/verticals';

export default function Vertical() {
    const { slug } = useParams();
    const v = VERTICALS[slug];
    if (!v) return <Navigate to="/" replace />;

    const nextSlug = VERTICAL_ORDER[(VERTICAL_ORDER.indexOf(slug) + 1) % VERTICAL_ORDER.length];
    const next = VERTICALS[nextSlug];

    return (
        <div data-testid={`vertical-page-${slug}`}>
            {/* Hero */}
            <section className="relative h-[85vh] min-h-[560px] overflow-hidden">
                <img src={v.hero} alt={v.title} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-aurika-obsidian/80 via-aurika-obsidian/25 to-aurika-obsidian/40" />
                <div className="grain absolute inset-0" />
                <div className="relative z-10 h-full container-editorial flex flex-col justify-between pt-40 pb-16 text-aurika-bone">
                    <div className="flex items-center justify-between">
                        <p className="overline text-aurika-ochre">{v.eyebrow}</p>
                        <span className="font-mono-brand text-sm tracking-widest">— {v.index} / 04 —</span>
                    </div>
                    <div className="max-w-5xl">
                        <h1 className="font-display font-light leading-[0.96] text-balance tracking-[-0.02em]
                            text-[3rem] sm:text-[4rem] md:text-[5.25rem] lg:text-[6.25rem]">
                            {v.title}
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg md:text-xl font-light text-aurika-bone/85 italic font-display">
                            {v.tagline}
                        </p>
                    </div>
                </div>
            </section>

            {/* Intro */}
            <section className="section-y bg-aurika-bone">
                <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <p className="overline">The Idea</p>
                        <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light text-aurika-obsidian leading-[1.05]">
                            What we design here.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="text-lg text-aurika-ink font-light leading-relaxed">{v.summary}</p>
                    </div>
                </div>
            </section>

            {/* Offerings */}
            <section className="section-y bg-aurika-linen">
                <div className="container-editorial">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-4 lg:sticky lg:top-32">
                            <p className="overline">What We Offer</p>
                            <h2 className="mt-4 font-display text-4xl md:text-5xl font-light text-aurika-obsidian leading-[1.05]">
                                Programs & <em className="italic">experiences</em> in this vertical.
                            </h2>
                            <Link to="/partner" data-testid={`vertical-cta-${slug}`} className="btn-primary mt-10">
                                Design one with us <ArrowRight size={16} />
                            </Link>
                        </div>

                        <ul className="lg:col-span-7 lg:col-start-6 divide-y divide-aurika-border border-t border-aurika-obsidian">
                            {v.offerings.map((o, i) => (
                                <li key={o} data-testid={`vertical-offering-${i}`} className="py-6 flex items-start gap-6 group">
                                    <span className="font-mono-brand text-xs text-aurika-terracotta pt-1 min-w-[2rem]">0{i + 1}</span>
                                    <span className="font-display text-2xl md:text-3xl font-light text-aurika-obsidian group-hover:text-aurika-terracotta transition-colors duration-300 flex-1">
                                        {o}
                                    </span>
                                    <Check size={18} className="text-aurika-muted mt-2" />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Gallery */}
            <section className="section-y bg-aurika-bone">
                <div className="container-editorial">
                    <p className="overline">From The Field</p>
                    <h2 className="mt-4 font-display text-4xl md:text-5xl font-light text-aurika-obsidian leading-[1.05]">
                        Glimpses.
                    </h2>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-7 aspect-[4/3] overflow-hidden">
                            <img src={v.gallery[0]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
                        </div>
                        <div className="md:col-span-5 aspect-[4/3] overflow-hidden">
                            <img src={v.gallery[1]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
                        </div>
                        <div className="md:col-span-12 aspect-[21/9] overflow-hidden">
                            <img src={v.gallery[2]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Metrics */}
            <section className="section-y bg-aurika-obsidian text-aurika-bone">
                <div className="container-editorial">
                    <p className="overline text-aurika-ochre">Our Impact Ambition</p>
                    <h2 className="mt-4 font-display text-4xl md:text-5xl font-light leading-[1.05]">
                        The outcomes we’re <em className="italic text-aurika-ochre">striving</em> for.
                    </h2>
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {v.metrics.map((m) => (
                            <div key={m.label} className="border-t border-aurika-bone/25 pt-6">
                                <div className="font-display text-6xl md:text-7xl font-light leading-none">
                                    {m.value}
                                </div>
                                <p className="mt-4 text-[11px] tracking-[0.2em] uppercase text-aurika-bone/70">{m.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Next vertical */}
            <section className="section-y bg-aurika-linen">
                <div className="container-editorial">
                    <Link
                        to={`/verticals/${nextSlug}`}
                        data-testid={`vertical-next-${nextSlug}`}
                        className="group grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
                    >
                        <div className="md:col-span-4 aspect-[4/5] overflow-hidden">
                            <img src={next.hero} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                        </div>
                        <div className="md:col-span-7 md:col-start-6">
                            <p className="overline">Next Vertical — {next.index}</p>
                            <h3 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl font-light text-aurika-obsidian leading-[1] group-hover:text-aurika-terracotta transition-colors duration-500">
                                {next.title}
                            </h3>
                            <span className="mt-6 inline-flex items-center gap-3 text-sm tracking-[0.16em] uppercase text-aurika-terracotta font-medium">
                                Continue exploring <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-500" />
                            </span>
                        </div>
                    </Link>
                </div>
            </section>
        </div>
    );
}
